import { useMemo } from "react";
import { App } from "antd";
import type {
  ArgsProps,
  MessageInstance,
  MessageType,
} from "antd/es/message/interface";
import { useConfiguration } from "../../../hooks/useConfiguration";

/**
 * Резултат на „показано" съобщение, което никога не е излизало на екрана —
 * пази контракта на antd (може да се `await`-не и да се затвори с извикване),
 * за да не се интересуват извикващите дали известията са включени.
 */
function silentMessage(): MessageType {
  const close = () => {};
  const resolved = Promise.resolve(true);
  return Object.assign(close, { then: resolved.then.bind(resolved) });
}

/**
 * `message` за модул „Маркиране" — ЕДИНСТВЕНИЯТ вход за toast-ове в
 * `pages/ordering/`, вместо `App.useApp().message`.
 *
 * Слуша настройката на ресторанта `orderingSuccessNotifications`
 * (Конфигурация → Маркиране): изключена ли е, `success` и `open` с
 * `type: "success"` не показват нищо. Всичко останало (`error`, `warning`,
 * `info`, `loading`, `destroy`) минава непроменено — грешките и
 * предупрежденията се показват ВИНАГИ, независимо от настройката.
 *
 * Капан: `open({ key, type: "success" })` подменя `loading` toast със същия
 * key (печатът на предварителна сметка). При спрени известия loading-ът
 * трябва да се махне изрично, иначе виси със спинера завинаги.
 */
export function useOrderingMessage(): MessageInstance {
  const { message } = App.useApp();
  const successEnabled =
    useConfiguration().configuration.orderingSuccessNotifications;

  return useMemo<MessageInstance>(() => {
    if (successEnabled) return message;
    return {
      info: message.info,
      error: message.error,
      warning: message.warning,
      loading: message.loading,
      destroy: message.destroy,
      success: () => silentMessage(),
      open: (config: ArgsProps) => {
        if (config.type !== "success") return message.open(config);
        if (config.key !== undefined) message.destroy(config.key);
        return silentMessage();
      },
    };
  }, [message, successEnabled]);
}
